import { motion } from 'framer-motion';
import { useEffect } from 'react';
import Navigation from '../home/components/Navigation';
import Footer from '../home/components/Footer';
import FloatingButtons from '../home/components/FloatingButtons';
import { useInView } from '../home/hooks/useInView';

const Tournefeuille = () => {
  const [heroRef, heroInView] = useInView({ threshold: 0.1 });
  const [servicesRef, servicesInView] = useInView({ threshold: 0.1 });
  const [testimonialsRef, testimonialsInView] = useInView({ threshold: 0.1 });
  const [seoRef, seoInView] = useInView({ threshold: 0.1 });

  useEffect(() => {
    document.title = 'Réparation de fenêtres à Tournefeuille – Intervention rapide 24h/24 | Vitrerie 31';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Expert réparation fenêtres à Tournefeuille (31170). Intervention sous 2h pour fenêtres PVC, bois, alu. Devis gratuit. ☎ 05 54 54 25 45');
    }
  }, []);

  const services = [
    { icon: 'ri-tools-line', title: 'Réparation Fenêtres PVC', desc: 'Joints, mécanismes, vitrage' },
    { icon: 'ri-hammer-line', title: 'Fenêtres Bois', desc: 'Rénovation et entretien' },
    { icon: 'ri-window-line', title: 'Fenêtres Aluminium', desc: 'Réparation et remplacement' },
    { icon: 'ri-shield-check-line', title: 'Vitrerie d\'urgence', desc: 'Intervention 24h/24' }
  ];

  const testimonials = [
    {
      name: 'Catherine Durand',
      location: 'Quartier Parc du Château',
      text: 'Fenêtre bloquée depuis plusieurs jours. Intervention rapide et professionnelle. Le technicien a pris le temps de tout expliquer.',
      rating: 5
    },
    {
      name: 'Michel Garnier',
      location: 'Centre-ville Tournefeuille',
      text: 'Remplacement de joints sur mes fenêtres PVC. Travail soigné, prix raisonnable. Je recommande sans hésiter !',
      rating: 5
    },
    {
      name: 'Nathalie Petit',
      location: 'Quartier Lafouasse',
      text: 'Problème d\'étanchéité résolu en moins de 2h. Équipe sympathique et efficace. Très satisfaite du service.',
      rating: 5
    }
  ];

  const problemesTypiques = [
    { icon: 'ri-water-flash-line', title: 'Infiltrations d\'eau', desc: 'Joints dégradés par l\'humidité' },
    { icon: 'ri-wind-line', title: 'Courants d\'air', desc: 'Isolation thermique défaillante' },
    { icon: 'ri-lock-unlock-line', title: 'Fermetures difficiles', desc: 'Mécanismes grippés ou cassés' },
    { icon: 'ri-contrast-drop-line', title: 'Condensation', desc: 'Double vitrage à remplacer' }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-teal-50 via-white to-orange-50"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={heroInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-6">
              <i className="ri-map-pin-line"></i>
              <span>Tournefeuille - Haute-Garonne (31170)</span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-gray-900 mb-6">
              Réparation de fenêtres à <strong className="text-teal-600">Tournefeuille</strong>
            </h1>
            
            <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Intervention rapide <strong>sous 2h</strong> pour tous vos problèmes de fenêtres
            </p>

            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <a
                href="tel:0554542545"
                className="inline-flex items-center gap-2 bg-teal-600 text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-teal-700 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
              >
                <i className="ri-phone-line text-xl"></i>
                05 54 54 25 45
              </a>
              <a
                href="#contact"
                className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer border-2 border-teal-600 active:scale-95"
              >
                <i className="ri-message-line text-xl"></i>
                Devis gratuit
              </a>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-time-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Intervention rapide</h3>
                <p className="text-gray-600 text-sm">Sous 2h à Tournefeuille</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-map-pin-2-line text-2xl text-orange-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">À 10 km de Toulouse</h3>
                <p className="text-gray-600 text-sm">Tous quartiers desservis</p>
              </div>
              <div className="bg-white rounded-lg p-6 shadow-lg">
                <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <i className="ri-24-hours-line text-2xl text-teal-600"></i>
                </div>
                <h3 className="font-bold text-gray-900 mb-2">Urgences 24h/24</h3>
                <p className="text-gray-600 text-sm">7 jours sur 7</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section ref={servicesRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={servicesInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Nos services à <strong className="text-teal-600">Tournefeuille</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Experts en réparation de fenêtres pour tous les quartiers
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={servicesInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 hover:shadow-xl transition-all cursor-pointer group"
              >
                <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center mb-4 group-hover:bg-teal-500 transition-colors">
                  <i className={`${service.icon} text-3xl text-teal-600 group-hover:text-white transition-colors`}></i>
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{service.title}</h3>
                <p className="text-gray-600">{service.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Problèmes Typiques */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Problèmes fréquents à <strong className="text-teal-600">Tournefeuille</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Nous connaissons les spécificités de votre commune
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {problemesTypiques.map((probleme, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-6 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mb-4">
                  <i className={`${probleme.icon} text-2xl text-orange-600`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{probleme.title}</h3>
                <p className="text-gray-600 text-sm">{probleme.desc}</p>
              </div>
            ))}
          </div>

          <div className="mt-12 bg-gradient-to-r from-teal-500 to-orange-500 rounded-lg p-8 text-white text-center">
            <h3 className="text-2xl font-bold mb-4">Pourquoi ces problèmes à Tournefeuille ?</h3>
            <p className="text-lg mb-6 opacity-90 max-w-3xl mx-auto">
              Située à <strong>10 km au sud-ouest de Toulouse</strong>, Tournefeuille bénéficie d'un climat océanique avec des précipitations régulières. Les quartiers résidentiels comme <strong>Parc du Château, Lafouasse et le Centre-ville</strong> sont particulièrement touchés par les problèmes d'infiltration et de condensation. Nos équipes connaissent parfaitement ces problématiques locales.
            </p>
          </div>
        </div>
      </section>

      {/* Témoignages Clients */}
      <section ref={testimonialsRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Avis de nos clients à <strong className="text-teal-600">Tournefeuille</strong>
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Ils nous ont fait confiance pour leurs fenêtres
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={testimonialsInView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gradient-to-br from-teal-50 to-orange-50 rounded-lg p-6 shadow-lg"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-500 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-4 italic">"{testimonial.text}"</p>
                <div className="border-t border-gray-200 pt-4">
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-sm text-gray-600 flex items-center gap-1">
                    <i className="ri-map-pin-line"></i>
                    {testimonial.location}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* NOUVEAU BLOC 1 - Expertise Fenêtres Anciennes */}
      <section ref={seoRef} className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={seoInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="relative"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/7eb2e3e47b46719d9d0b4777f9b01213.jpg"
                  alt="Réparation fenêtres anciennes Tournefeuille"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-teal-900/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-6 left-6 bg-white rounded-xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-home-4-line text-teal-600 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Patrimoine</p>
                      <p className="text-sm text-gray-600">Expert restauration</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={seoInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <strong className="text-teal-600">Restauration de Fenêtres Anciennes</strong> à Tournefeuille
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Tournefeuille possède un riche <strong>patrimoine architectural</strong> avec de nombreuses maisons anciennes et bâtisses traditionnelles. Notre équipe est spécialisée dans la <strong>restauration de fenêtres anciennes à Tournefeuille</strong>, préservant le cachet authentique de votre habitation tout en améliorant ses performances énergétiques.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nous intervenons pour la <strong>réparation de fenêtres en bois anciennes</strong>, le <strong>remplacement de vitrage simple par du double vitrage</strong> sur mesure, la <strong>restauration de volets battants</strong> et la <strong>rénovation de menuiseries d'époque</strong>. Notre savoir-faire artisanal garantit une intervention respectueuse du style architectural de votre maison à Tournefeuille.
              </p>

              <div className="space-y-3 mb-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-paint-brush-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Traitement et Restauration du Bois</h4>
                    <p className="text-gray-600 text-sm">Ponçage, traitement anti-humidité, lasure et peinture traditionnelle pour vos fenêtres anciennes</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-layout-grid-line text-2xl text-orange-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Double Vitrage sur Mesure</h4>
                    <p className="text-gray-600 text-sm">Installation de double vitrage adapté aux châssis anciens sans dénaturer l'esthétique</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center mr-4 flex-shrink-0">
                    <i className="ri-tools-line text-2xl text-teal-600"></i>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900 mb-1">Réparation de Mécanismes Anciens</h4>
                    <p className="text-gray-600 text-sm">Remise en état de crémones, espagnolettes et ferrures d'époque</p>
                  </div>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-teal-500 to-teal-600 text-white px-8 py-4 rounded-full font-semibold hover:from-teal-600 hover:to-teal-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Devis Restauration Gratuit
              </a>
            </motion.div>
          </div>

          {/* NOUVEAU BLOC 2 - Isolation Thermique */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-20">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={seoInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="order-2 lg:order-1"
            >
              <h3 className="text-3xl font-bold text-gray-900 mb-6">
                <strong className="text-orange-600">Amélioration de l'Isolation Thermique</strong> à Tournefeuille
              </h3>
              <p className="text-gray-600 mb-4 leading-relaxed">
                Les hivers peuvent être rigoureux à Tournefeuille et les étés chauds. Une bonne <strong>isolation thermique de vos fenêtres</strong> est essentielle pour réduire vos factures énergétiques et améliorer votre confort. Nous proposons des solutions d'<strong>isolation de fenêtres à Tournefeuille</strong> adaptées à tous types de menuiseries.
              </p>
              <p className="text-gray-600 mb-6 leading-relaxed">
                Nos interventions incluent le <strong>remplacement de joints d'étanchéité</strong>, l'<strong>installation de double vitrage haute performance</strong>, le <strong>calfeutrage de fenêtres</strong> et l'<strong>ajout de survitrage</strong>. Nous vous conseillons sur les meilleures solutions pour optimiser l'isolation de votre habitation à Tournefeuille tout en respectant votre budget.
              </p>

              <div className="bg-gradient-to-r from-orange-50 to-teal-50 rounded-xl p-6 mb-6">
                <h4 className="font-bold text-gray-900 mb-4 flex items-center">
                  <i className="ri-temp-cold-line text-orange-600 mr-2 text-xl"></i>
                  Économies d'Énergie Garanties
                </h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-orange-600 mb-1">30%</p>
                    <p className="text-sm text-gray-600">Réduction chauffage</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <p className="text-3xl font-bold text-teal-600 mb-1">25%</p>
                    <p className="text-sm text-gray-600">Réduction climatisation</p>
                  </div>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="flex items-center text-gray-700">
                  <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
                  <span>Double vitrage à isolation renforcée (VIR)</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
                  <span>Joints d'étanchéité haute performance</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
                  <span>Survitrage pour fenêtres existantes</span>
                </div>
                <div className="flex items-center text-gray-700">
                  <i className="ri-check-line text-orange-600 mr-3 text-xl"></i>
                  <span>Conseil personnalisé et bilan thermique</span>
                </div>
              </div>

              <a
                href="#contact"
                className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-8 py-4 rounded-full font-semibold hover:from-orange-600 hover:to-orange-700 transition-all shadow-lg hover:shadow-xl hover:scale-105 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-phone-line mr-2"></i>
                Bilan Thermique Gratuit
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={seoInView ? { opacity: 1, x: 0 } : {}}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="relative order-1 lg:order-2"
            >
              <div className="relative w-full h-[500px] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://readdy.ai/api/search-image?query=modern%20energy%20efficient%20double%20glazed%20window%20with%20thermal%20insulation%20showing%20cross%20section%20detail%20of%20insulating%20glass%20unit%20low%20emissivity%20coating%20argon%20gas%20fill%20professional%20installation%20in%20contemporary%20home%20demonstrating%20heat%20retention%20technology%20simple%20clean%20background&width=800&height=600&seq=tournefeuille-thermal-insulation-v1&orientation=landscape"
                  alt="Isolation thermique fenêtres Tournefeuille"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-orange-900/60 via-transparent to-transparent"></div>
                
                <div className="absolute bottom-6 right-6 bg-white rounded-xl shadow-xl p-4">
                  <div className="flex items-center">
                    <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3 flex-shrink-0">
                      <i className="ri-fire-line text-orange-600 text-2xl"></i>
                    </div>
                    <div>
                      <p className="text-lg font-bold text-gray-900">Isolation</p>
                      <p className="text-sm text-gray-600">Haute performance</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quartiers desservis */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tous les quartiers de <strong className="text-teal-600">Tournefeuille</strong>
            </h2>
            <p className="text-lg text-gray-600">Intervention rapide dans toute la commune</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {['Centre-ville', 'Parc du Château', 'Lafouasse', 'Monges', 'Fond de Rouze', 'Violettes', 'Padoue', 'Terrasse'].map((quartier, index) => (
              <div
                key={index}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-all text-center"
              >
                <i className="ri-map-pin-2-fill text-2xl text-teal-600 mb-2"></i>
                <p className="font-semibold text-gray-900">{quartier}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-teal-500 to-orange-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Besoin d'une réparation de fenêtre à Tournefeuille ?
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Intervention rapide sous 2h - Devis gratuit - Urgences 24h/24
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="tel:0554542545"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-full font-semibold text-lg hover:bg-gray-50 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-phone-line text-xl"></i>
              Appelez maintenant
            </a>
            <a
              href="/#contact"
              className="inline-flex items-center gap-2 bg-transparent border-2 border-white text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-white/10 transition-all shadow-lg hover:scale-105 whitespace-nowrap cursor-pointer active:scale-95"
            >
              <i className="ri-message-line text-xl"></i>
              Demander un devis
            </a>
          </div>
        </div>
      </section>

      {/* Liens vers autres villes */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Nous intervenons aussi dans les villes voisines
          </h3>
          <div className="flex flex-wrap justify-center gap-4">
            <a href="/" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Toulouse</a>
            <span className="text-gray-400">•</span>
            <a href="/colomiers" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Colomiers</a>
            <span className="text-gray-400">•</span>
            <a href="/blagnac" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Blagnac</a>
            <span className="text-gray-400">•</span>
            <a href="/muret" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Muret</a>
            <span className="text-gray-400">•</span>
            <a href="/balma" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Balma</a>
            <span className="text-gray-400">•</span>
            <a href="/ramonville" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Ramonville</a>
            <span className="text-gray-400">•</span>
            <a href="/cugnaux" className="text-teal-600 hover:text-teal-700 font-semibold hover:underline cursor-pointer">Cugnaux</a>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingButtons />
    </div>
  );
};

export default Tournefeuille;
